import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ATTACHMENT_KINDS, type AttachmentKind } from '@jupi/shared'

// Attach a release certificate, invoice or photo to a stock lot. PDF, JPEG or
// PNG; the server sniffs the content and refuses anything else.
export function AttachmentDialog({
  open,
  saving,
  onUpload,
  onClose,
}: {
  open: boolean
  saving: boolean
  onUpload: (kind: AttachmentKind, file: File) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [kind, setKind] = useState<AttachmentKind>('certificate')
  const [file, setFile] = useState<File | null>(null)
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (open) {
      setKind('certificate')
      setFile(null)
    }
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('inventory.attachTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label={t('inventory.attachKind')} value={kind} onChange={(e) => setKind(e.target.value as AttachmentKind)}>
            {ATTACHMENT_KINDS.map((k) => (
              <MenuItem key={k} value={k}>
                {t(`inventory.attachKinds.${k}`)}
              </MenuItem>
            ))}
          </TextField>
          <input
            ref={input}
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            hidden
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <Button variant="outlined" onClick={() => input.current?.click()}>
            {t('inventory.attachFile')}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {file ? file.name : t('inventory.attachNone')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!file || saving} onClick={() => file && onUpload(kind, file)}>
          {t('inventory.upload')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
